import { captureException } from "@sentry/nextjs";
import type { LeadConversion } from "@portal/app/_common/lib/leadConversion";

type HostedLeadConversion = Omit<
  Extract<LeadConversion, { leadSource: "form_funnel_hosted" }>,
  "leadSource"
>;

interface DataLayerEvent {
  event?: string;
  hosted_lead?: { transaction_id?: string };
}

export const HOSTED_LEAD_TRACKING_TIMEOUT_MS = 2000;

/** Queue the lead for GTM; Google tags and their consent checks live in GTM. */
export async function trackHostedLeadConversion({
  enrollmentItemId,
  funnelId,
  programId,
  providerId,
}: HostedLeadConversion): Promise<void> {
  let timeout: ReturnType<typeof setTimeout> | undefined;
  try {
    // Queue synchronously so a completion callback/unmount cannot drop the event.
    window.dataLayer ||= [];
    const dataLayer = window.dataLayer;

    // Repeated submissions/remounts can return the same enrollment. Avoid
    // duplicates on this page without adding cookies or browser storage.
    const alreadyTracked = dataLayer.some(
      (entry: DataLayerEvent | null) =>
        entry?.event === "generate_lead" && entry.hosted_lead?.transaction_id === enrollmentItemId
    );
    if (alreadyTracked) return;

    await new Promise<void>((resolve) => {
      // Bound the wait even if a loaded GTM container never invokes the callback.
      timeout = setTimeout(resolve, HOSTED_LEAD_TRACKING_TIMEOUT_MS);
      dataLayer.push({
        event: "generate_lead",
        completed_lead: null,
        hosted_lead: {
          lead_source: "form_funnel_hosted",
          transaction_id: enrollmentItemId,
          form_funnel_id: funnelId,
          program_id: programId,
          provider_id: providerId,
        },
        eventCallback: resolve,
        eventTimeout: HOSTED_LEAD_TRACKING_TIMEOUT_MS,
      });
      // Still queue for late-loading GTM, but do not hold navigation for an absent
      // (possibly blocked) container. Other trackers retain their 100 ms flush delay.
      if (!(window as Window & { google_tag_manager?: unknown }).google_tag_manager) resolve();
    });
  } catch (error) {
    // A tracking failure must not turn an accepted lead into a submission error.
    captureException(error);
  } finally {
    clearTimeout(timeout);
  }
}
